/**
 * useIsMobileChrome — viewport hook that decides which chrome to show.
 *
 * The chrome (PreferencesBar + BackToGamesBar) ships two presentations:
 *  - Desktop: inline pill clusters with text labels (above 768px)
 *  - Mobile: single 44px circular icons + a popout for preferences
 *
 * The 768px threshold matches the brief — the inline pill cluster works
 * down to about there, below it the cluster crowds the content and we
 * switch to a single-icon trigger with a designed popout panel.
 *
 * The threshold is intentionally NOT the responsive.css 430px phone
 * breakpoint. Phone-portrait + tablet-portrait both want the popout
 * pattern; phone-landscape + tablet-landscape are already on the desktop
 * presentation.
 *
 * Returns `false` during SSR and on viewports wider than 768px.
 */
import { useEffect, useState } from 'react';

const MOBILE_CHROME_QUERY = '(max-width: 768px)';

export function useIsMobileChrome(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(MOBILE_CHROME_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(MOBILE_CHROME_QUERY);
    const onChange = (): void => {
      setIsMobile(mql.matches);
    };
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}
